/**
 * Password-gated confirm for full-device location sign-out (GroupPicker +
 * RelockSheet). A plain confirm used to be enough, but mid-session exit is
 * now reachable from the teacher switch sheet — requiring the location
 * password keeps floor staff from ejecting a shared iPad they cannot
 * re-open. Verifies via signInWithPassword against the current session
 * email, then runs the caller's signOutLocation flow.
 */

import { useEffect, useState, type FormEvent } from 'react';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { verifyLocationPassword } from '../utils/verifyLocationPassword';

export interface SignOutConfirmProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Runs only after the location password checks out. May reject. */
  onConfirm: () => void | Promise<void>;
  isSigningOut: boolean;
}

export function SignOutConfirm({ open, onOpenChange, onConfirm, isSigningOut }: SignOutConfirmProps) {
  const { user } = useAuth();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);

  const busy = isVerifying || isSigningOut;

  useEffect(() => {
    if (!open) {
      setPassword('');
      setError(null);
      setIsVerifying(false);
    }
  }, [open]);

  async function handleSubmit(e?: FormEvent): Promise<void> {
    e?.preventDefault();
    if (busy) return;

    setIsVerifying(true);
    setError(null);
    try {
      await verifyLocationPassword(user?.email ?? '', password);
      await onConfirm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign out failed — try again');
      setIsVerifying(false);
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={(next) => { if (!busy) onOpenChange(next); }}>
      <AlertDialogContent>
        <form onSubmit={(e) => { void handleSubmit(e); }}>
          <AlertDialogHeader>
            <AlertDialogTitle>Sign out of this device?</AlertDialogTitle>
            <AlertDialogDescription>
              This signs the whole device out, not just you. Enter the location password to confirm —
              without it the tablet stays signed in.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-2 py-2">
            <Label htmlFor="location-signout-password">Location password</Label>
            <Input
              id="location-signout-password"
              type="password"
              autoComplete="current-password"
              autoFocus
              placeholder="Enter location password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
              aria-invalid={!!error}
            />
            {error && (
              <p className="text-sm font-medium text-destructive" role="alert">
                {error}
              </p>
            )}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel type="button" disabled={busy}>
              Cancel
            </AlertDialogCancel>
            {/* Plain Button — AlertDialogAction auto-closes and would skip password verify. */}
            <Button type="submit" disabled={busy || !password}>
              {busy ? 'Signing out…' : 'Yes, sign out'}
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
